/**
 * The device rail's transport badge: how the tunnel is reaching its hub right now.
 * `relay` is the TCP-over-WebSocket fallback (the device's UDP is not getting through), so it
 * is amber, not red: the device works, on its worse path. `udp` is the normal path. No badge
 * when the server does not know (`transport` null).
 *
 * Pure, so `scripts/check-vpn-transport.mjs` can pin the wording and the colours.
 */
export interface TransportFields {
  transport?: string | null;
  relayRegion?: string | null;
  relaySince?: string | null;
}

export function transportBadge(
  p?: TransportFields | null,
): { label: string; title: string; className: string } | null {
  if (p?.transport === 'relay') {
    const since = p.relaySince ? new Date(p.relaySince) : null;
    const at =
      since && !Number.isNaN(since.getTime())
        ? since.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
        : null;
    return {
      label: ['TCP relay', p.relayRegion, at ? `since ${at}` : null].filter(Boolean).join(' · '),
      title:
        'The device is reaching its hub through wg-relay (WireGuard over a TCP WebSocket) because UDP is not getting through.',
      className: 'health-amber',
    };
  }
  if (p?.transport === 'udp') {
    return {
      label: 'UDP',
      title: 'Direct WireGuard over UDP: no relay session, and the gateway saw a recent handshake.',
      className: 'health-green',
    };
  }
  return null;
}

/**
 * The devices tab's Transport filter. Only `relay` exists server-side (a `udp` filter would
 * need every device's fresh-handshake state, which is not a bounded read), so the control is
 * "All / TCP relay". The value lives in the URL (`?dtr=relay`) so a refresh keeps it.
 */
export type TransportFilter = 'relay';

/** URL key for the device-tab transport filter (siblings: `dq`, `dpage`, `dper`). */
export const TRANSPORT_PARAM = 'dtr';

/** Anything but a known value is "no filter": a hand-edited URL must not 400 the query. */
export function parseTransportFilter(raw?: string | null): TransportFilter | null {
  return raw === 'relay' ? 'relay' : null;
}

/** The `vpnDevicePeers` filter argument, or undefined when nothing narrows the list. */
export function devicePeerFilter(
  search: string,
  rawRegions: string[],
  transport: TransportFilter | null,
  enrolledAfter: string | null = null,
): { search?: string; regions?: string[]; transport?: TransportFilter; enrolledAfter?: string } | undefined {
  if (!search && !rawRegions.length && !transport && !enrolledAfter) return undefined;
  return {
    search: search || undefined,
    regions: rawRegions.length ? rawRegions : undefined,
    transport: transport ?? undefined,
    // Only present when set, so the three-filter shape stays exactly what it was.
    ...(enrolledAfter ? { enrolledAfter } : {}),
  };
}

export interface RelaySummaryFields {
  count?: number | null;
  truncated?: boolean | null;
}

/**
 * "N on relay" for the page header chip. null = say nothing: the count is unknown (Redis
 * unreachable), and "0 on relay" would be a claim the server never made. A truncated scan
 * is a lower bound, so it reads "N+".
 */
export function relayChipText(s?: RelaySummaryFields | null): string | null {
  if (s?.count === null || s?.count === undefined) return null;
  return `${s.count.toLocaleString('en-US')}${s.truncated ? '+' : ''} on relay`;
}

/** "1 device" / "2 devices" for the Devices tab count (the filtered view often shows exactly one). */
export function deviceCountText(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'device' : 'devices'}`;
}
